export const DONATION_SCHEME_AMOUNT_TYPE_OPTIONS = [
  { value: "variable", label: "Variable" },
  { value: "fixed", label: "Fixed" },
] as const;

export type DonationSchemeAmountType =
  (typeof DONATION_SCHEME_AMOUNT_TYPE_OPTIONS)[number]["value"];

export const DONATION_SCHEME_MAX_AMOUNT = 10000000;

export interface DonationSchemePayload {
  title: string;
  sub_title?: string;
  type_of_donation: number;
  category: number;
  sub_category: number;
  description?: string;
  amount_needed: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  banner_image?: File | string;
  allow_donation_after_target?: boolean;
  amount_type: DonationSchemeAmountType;
  fixed_amount?: string;
  min_contribution?: string;
  max_contribution?: string;
}

export interface DonationSchemeRecord
  extends Omit<
    DonationSchemePayload,
    "fixed_amount" | "min_contribution" | "max_contribution"
  > {
  id: number;
  amount_collected?: string | number | null;
  fixed_amount?: string | number | null;
  min_contribution?: string | number | null;
  max_contribution?: string | number | null;
  type_of_donation_name?: string;
  category_name?: string;
  sub_category_title?: string;
  sub_category_name?: string;
  date_status?: string;
  /** @deprecated Legacy display fields from older API responses */
  donation_type_name?: string;
  main_category_name?: string;
  created_at?: string;
  updated_at?: string;
}

export function formatDonationSchemeAmount(
  value?: string | number | null
): string {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) {
    return "—";
  }
  return `₹${amount.toLocaleString("en-IN")}`;
}

export function toDonationSchemeAmount(value: unknown): string | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return undefined;
  }
  return num.toFixed(2);
}

export function getDonationSchemeAmountError(
  value: unknown,
  label: string,
  options?: { required?: boolean }
): string | undefined {
  if (value === undefined || value === null || value === "") {
    return options?.required ? `${label} is required.` : undefined;
  }

  const num = Number(value);
  if (!Number.isFinite(num)) {
    return `Enter a valid ${label.toLowerCase()}.`;
  }

  if (num < 0) {
    return `${label} cannot be negative.`;
  }

  if (num > DONATION_SCHEME_MAX_AMOUNT) {
    return `${label} cannot exceed ₹1 Cr (1,00,00,000).`;
  }

  return undefined;
}

export function validateDonationSchemeAmount(
  value: unknown,
  label: string
): string | undefined {
  const error = getDonationSchemeAmountError(value, label);
  if (error) {
    throw new Error(error);
  }
  return toDonationSchemeAmount(value);
}
